/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { isAndroid } from "react-device-detect";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { ReactComponent as CopyIcon } from "assets/images/icon_copy-attach.svg";
import { ReactComponent as CloseIcon } from "assets/images/icon_delete_input.svg";
import { ReactComponent as PointOfSaleIcon } from "assets/images/icon_point-of-sale.svg";

import { actionCreators } from "store/Notification";

import { isInAppBrowser } from "shared/constants/isInAppBrowser";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

import { styles } from "./styles";
import { grey800, mainStyles } from "styles";

interface Props {
  url: string;
  open: boolean;
  onClose: () => void;
  showChromeButton?: boolean;
}

const InfoModal: React.FC<Props> = (props) => {
  const { url, open, showChromeButton, onClose } = props;
  const dispatch = useDispatch();

  const inAppBrowser = isInAppBrowser();
  const timestamp = new Date().getTime();

  useEffect(() => {
    analytics().sendEvent(AnalyticsEvents.iban_qr_pay_modal_open);
  }, []);

  const onCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url + `?t=${timestamp}`);
      analytics().sendEvent(AnalyticsEvents.iban_qr_pay_modal_copy);

      dispatch(
        actionCreators.setNotification({
          message: "Посилання скопійовано",
          severity: "success",
        })
      );
    } catch (err) {
      dispatch(
        actionCreators.setNotification({
          message: "Трапилась помилка при копіюванні. Спробуйте пізніше",
          severity: "error",
        })
      );
    }
  };

  const constructChromeUrl = () => {
    const chromeUrl =
      url.replace("https://", "intent://") +
      `?t=${timestamp}#Intent;scheme=https;package=com.android.chrome;end`;

    return chromeUrl;
  };

  const onOpenInChrome = () => {
    analytics().sendEvent(AnalyticsEvents.iban_qr_pay_modal_chrome);
  };

  const onCloseModal = () => {
    analytics().sendEvent(AnalyticsEvents.iban_qr_pay_modal_close);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: styles.modal }}>
      <DialogTitle sx={styles.title}>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.content}>
        <PointOfSaleIcon />
        <Typography sx={mainStyles.h5} mt={3}>
          {inAppBrowser ? "Ой, щось не так!" : "Ой, щось не так!"}
        </Typography>
        <Typography sx={[mainStyles.body1, { color: grey800 }]} mt={0.5}>
          {inAppBrowser
            ? "Налаштування вашого додатку не дозволяє нам автоматично перенести дані для оплати."
            : "Ваш веб-переглядач незміг автоматично перенести дані для оплати."}
        </Typography>
        <Typography sx={[mainStyles.body1, { color: grey800 }]} mt={3}>
          {inAppBrowser
            ? "Скопіюйте посилання на оплату та відкрийте його у іншому веб-переглядачі на вашому пристрої (наприклад, Chrome або Safari)."
            : "Переконайтеся, що додаток встановлено, або відкрийте посилання на оплату в іншому веб-переглядачі на вашому пристрої (Chrome або Safari)."}
        </Typography>
        {inAppBrowser && (
          <>
            <Button
              sx={[mainStyles.secondaryButton, { marginTop: "16px" }]}
              size="medium"
              variant="contained"
              fullWidth
              onClick={onCopyLink}
            >
              <>
                <CopyIcon />
                <Typography sx={mainStyles.secondaryButtonText} ml={1.5}>
                  Копіювати посилання
                </Typography>
              </>
            </Button>

            {isAndroid && showChromeButton && (
              <Button
                sx={[mainStyles.primaryButton, { marginTop: "16px" }]}
                size="medium"
                variant="contained"
                fullWidth
                onClick={onOpenInChrome}
                href={constructChromeUrl()}
              >
                <Typography sx={mainStyles.primaryButtonText}>
                  Відкрити в Chrome
                </Typography>
              </Button>
            )}
          </>
        )}

        <Button
          sx={[mainStyles.secondaryButton, { marginTop: "16px" }]}
          size="medium"
          variant="contained"
          fullWidth
          onClick={onCloseModal}
        >
          <Typography sx={mainStyles.secondaryButtonText}>Закрити</Typography>
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default InfoModal;
